/**
 * @file useCreateGalleryImage.js
 */

import { useMutation } from "@apollo/client";
import { CREATE_GALLERY_IMAGE } from "cache/GalleryImage/mutations"; // Update the path as per your project structure

/**
 * @hook useCreateGalleryImage
 *
 * @description
 * Custom hook that handles the functionality of creating a gallery image.
 * This hook is used in components where a new gallery image needs to be created.
 *
 * @returns
 * - createGalleryImageExecute: Method to execute the create gallery image mutation
 * - loading: Boolean indicating the loading state
 * - error: Error object if there's an error
 */
const useCreateGalleryImage = () => {

	// Initialize the Apollo useMutation hook
	const [createGalleryImageMutation, { loading, error }] = useMutation(CREATE_GALLERY_IMAGE);

	/**
	 * Method to execute the create gallery image mutation.
	 *
	 * @param {Object} input - The input object for creating a gallery image.
	 * @property {String} input.title - The title of the gallery image.
	 * @property {String} input.description - The description of the gallery image.
	 * @property {String} input.image - The image of the gallery image.
	 * @returns {Object} - An object containing a Boolean indicating the mutation result or an error object.
	 * @throws {Error} - Throws an error if the mutation execution fails.
	 */
	const createGalleryImage = async (input) => {
		try {
			const { data } = await createGalleryImageMutation({
				variables: {
					input
				}
			});
			if (error) {
				throw new Error(error);
			}
			return { data: data.createGalleryImage };
		} catch (error) {
			return { error };
		}
	};

	return { createGalleryImage, loading, error };

};

export default useCreateGalleryImage;
